import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { User } from 'src/app/domain/user/types/User.model';
import { AvatarComponent } from 'src/app/ui-kit/avatar/avatar.component';
import { CardComponent } from 'src/app/ui-kit/card/card/card.component';
import { ChipComponent } from 'src/app/ui-kit/chip/chip.component';
import { IconComponent } from 'src/app/ui-kit/icon/icon.component';

/**
 * Canonical card for a Blume {@link User} (practitioner). Displays the user's avatar,
 * full name, job title, role, and caseload size with a status chip reflecting invite
 * and activation state.
 *
 * @security FERPA — `email`, `firstName`, `lastName` are PII. Do not log or transmit
 * these fields outside the render path.
 *
 * @feature shared
 */
@Component({
  selector: 'app-user-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AvatarComponent, CardComponent, ChipComponent, IconComponent],
  host: { class: 'block' },
  template: `
    <app-card
      [button]="clickable()"
      [ariaLabel]="'Open ' + fullName() + ' profile'"
      (click)="onClick()"
    >
      <div class="flex items-start gap-3">
        <app-avatar [name]="fullName()" size="lg" />

        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-2">
            <h3 class="truncate text-base font-semibold text-body">{{ fullName() }}</h3>
            @if (statusChip()) {
              <app-chip [color]="statusChip()!.color" size="xs">{{ statusChip()!.label }}</app-chip>
            }
          </div>

          @if (subtitle()) {
            <p class="truncate text-sm text-muted">{{ subtitle() }}</p>
          }

          @if (user().email) {
            <p class="mt-1 truncate text-xs text-muted">{{ user().email }}</p>
          }
        </div>

        @if (clickable()) {
          <app-icon name="chevron-forward" size="xs" color="medium" />
        }
      </div>

      @if (caseloadSize() > 0) {
        <div class="mt-3 flex items-center gap-2 border-t border-outline-low pt-3">
          <app-icon name="people" size="xs" color="medium" />
          <span class="text-sm text-muted">{{ caseloadLabel() }}</span>
        </div>
      }
    </app-card>
  `,
})
export class UserCardComponent {
  /** The user to display. @security FERPA — contains PII. */
  readonly user = input.required<User>();

  /** Whether the card is interactive. @default true */
  readonly clickable = input<boolean>(true);

  /** Fires when the card is clicked. */
  readonly cardClick = output<User>();

  readonly fullName = computed(() => {
    const u = this.user();
    const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
    return name || u.email || 'Unknown user';
  });

  readonly subtitle = computed(() => {
    const u = this.user();
    const parts: string[] = [];
    if (u.jobTitle) parts.push(u.jobTitle);
    if (u.school) parts.push(u.school);
    return parts.join(' · ');
  });

  readonly caseloadSize = computed(() => this.user().studentIds?.length ?? 0);

  readonly caseloadLabel = computed(() => {
    const n = this.caseloadSize();
    return `${n} student${n === 1 ? '' : 's'}`;
  });

  readonly statusChip = computed<{ label: string; color: 'danger' | 'warning' | 'success' } | null>(() => {
    const u = this.user();
    if (u.deactivated) return { label: 'Deactivated', color: 'danger' };
    if (u.inviteAccepted === false) return { label: 'Invited', color: 'warning' };
    return null;
  });

  onClick(): void {
    if (this.clickable()) {
      this.cardClick.emit(this.user());
    }
  }
}
